import { ReactElement } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartCard from '../ChartCard';

interface WindSpeedPlotProps {
  configs: any[];
  utcOffsetSeconds: number;
  weatherControllerDict: any;
  height?: number;
}

export default function WindSpeedPlot({
  configs,
  utcOffsetSeconds,
  weatherControllerDict,
  height = 300,
}: WindSpeedPlotProps): ReactElement {
  const probeData: any[] = [];

  // Collect average wind speed data from all runs (probe)
  for (const config of configs) {
    for (const run of config.runs || []) {
      const avgWindSpeed = run.average?.WindAbs_vG3?.[0];
      const runTime = run.average?.GatesStartTs_tG3?.[0];

      if (avgWindSpeed == null || runTime == null) {
        continue;
      }

      probeData.push({
        x: (runTime + utcOffsetSeconds) * 1000, // Convert to milliseconds
        y: avgWindSpeed * 3.6, // Convert m/s to km/h
      });
    }
  }

  // Collect Weather API wind speed data (unscaled initially)
  const apiData: any[] = [];
  const weatherApiSource = weatherControllerDict?.weather_api;
  if (weatherApiSource?.weather_data) {
    const validData = weatherApiSource.weather_data
      .filter((d: any) => d.Wind_v !== null && d.TimeAbs_t !== null)
      .sort((a: any, b: any) => a.TimeAbs_t - b.TimeAbs_t);

    for (const d of validData) {
      apiData.push({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000,
        y: d.Wind_v * 3.6, // Convert m/s to km/h
      });
    }
  }

  if (probeData.length === 0 && apiData.length === 0) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500" style={{ height: `${height}px` }}>
        No wind speed data available
      </div>
    );
  }

  // Sort by time
  probeData.sort((a, b) => a.x - b.x);

  // Calculate scaling factor for Weather API data
  let apiScalingFactor = 1;
  let apiLabel = 'Weather API Wind Speed';

  if (probeData.length > 0 && apiData.length > 0) {
    // Calculate average probe wind speed and time
    const avgProbeSpeed = probeData.reduce((sum, d) => sum + d.y, 0) / probeData.length;
    const avgProbeTime = probeData.reduce((sum, d) => sum + d.x, 0) / probeData.length;

    // Find nearest Weather API point to average probe time
    let nearestApiPoint = apiData[0];
    let minTimeDiff = Math.abs(apiData[0].x - avgProbeTime);

    for (const point of apiData) {
      const timeDiff = Math.abs(point.x - avgProbeTime);
      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        nearestApiPoint = point;
      }
    }

    // Calculate and round scaling factor
    const rawScalingFactor = nearestApiPoint.y / avgProbeSpeed;
    apiScalingFactor = Math.round(rawScalingFactor);

    // Only apply scaling if factor is valid and not 1
    if (apiScalingFactor > 0 && apiScalingFactor !== 1) {
      // Scale all API data points
      apiData.forEach((d) => {
        d.y = d.y / apiScalingFactor;
      });
      apiLabel = `Weather API Wind Speed / ${apiScalingFactor}`;
    }
  }

  // Calculate time range: 90 minutes before first probe run and 90 minutes after last probe run
  const minTime = probeData.length > 0 ? probeData[0].x : apiData.length > 0 ? apiData[0].x : Date.now();
  const maxTime =
    probeData.length > 0
      ? probeData[probeData.length - 1].x
      : apiData.length > 0
        ? apiData[apiData.length - 1].x
        : Date.now();
  const ninetyMinutesMs = 90 * 60 * 1000;

  const seriesData: any[] = [];
  const colorArray: string[] = [];

  if (probeData.length > 0) {
    seriesData.push({
      name: 'Probe Wind Speed',
      data: probeData,
    });
    colorArray.push('#10b981'); // Green
  }

  if (apiData.length > 0) {
    seriesData.push({
      name: apiLabel,
      data: apiData,
    });
    colorArray.push('#3b82f6'); // Blue
  }

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: height,
      zoom: { enabled: true, allowMouseWheelZoom: false },
      toolbar: { show: true },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
    },
    colors: colorArray,
    xaxis: {
      type: 'datetime',
      min: minTime - ninetyMinutesMs,
      max: maxTime + ninetyMinutesMs,
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Wind Speed (km/h)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(1),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} km/h`,
      },
    },
    title: {
      text: 'Wind Speed',
      align: 'center',
    },
  };

  return (
    <ChartCard height={`${height}px`} padding>
      <Chart options={options} series={seriesData} type="line" height="100%" />
    </ChartCard>
  );
}
